
import React, { useEffect, useState } from 'react';
import { 
  Copy, DollarSign, Users, Zap, ShieldCheck, Clock, Calendar, Check
} from 'lucide-react';
import { UserTier, PACKAGES } from '../types';
import { useSignalData } from '../context/SignalContext';

export const AffiliateDashboard: React.FC = () => {
  const { stats, transactions, currentUser, renewSubscription } = useSignalData();
  const [refLink, setRefLink] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (currentUser) {
        setRefLink(`${window.location.origin}/?ref=${currentUser.tradingViewId || currentUser.referralCode}`);
    }
  }, [currentUser]);

  const handleCopy = () => {
    navigator.clipboard.writeText(refLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const getTierName = (tier: UserTier) => {
      if(tier === UserTier.LEVEL_3) return 'Level 3';
      if(tier === UserTier.LEVEL_2) return 'Level 2';
      return 'Level 1';
  };

  const handleRenew = async (pkgId: string) => {
    const res = await renewSubscription(pkgId, true);
    alert(res.message);
  };

  const formatDate = (dateStr: string | undefined) => {
    if(!dateStr) return 'N/A';
    try {
        const d = new Date(dateStr);
        return d.toLocaleDateString('vi-VN', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    } catch (e) {
        return dateStr;
    }
  };

  return (
    <div className="space-y-6">
      {/* Account Info Bar */}
      {currentUser && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-white px-5 py-4 rounded-xl border border-slate-100 shadow-sm flex items-center gap-4">
             <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center text-blue-600">
                <Calendar className="w-5 h-5" />
             </div>
             <div>
                <p className="text-[10px] text-slate-400 font-black uppercase tracking-widest">Ngày đăng ký</p>
                <p className="text-base font-bold text-slate-800">{formatDate(currentUser.registerDate)}</p>
             </div>
          </div>
          <div className="bg-white px-5 py-4 rounded-xl border border-slate-100 shadow-sm flex items-center gap-4">
             <div className="w-10 h-10 rounded-full bg-orange-50 flex items-center justify-center text-orange-600">
                <Clock className="w-5 h-5" />
             </div>
             <div>
                <p className="text-[10px] text-slate-400 font-black uppercase tracking-widest">Hết hạn tài khoản</p>
                <p className="text-base font-bold text-slate-800">{formatDate(currentUser.subscriptionExpiry)}</p>
             </div>
          </div>
        </div>
      )}

      {/* Referral Link Card */}
      <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-100 overflow-hidden relative">
        <div className="absolute top-0 right-0 w-32 h-32 bg-brand-50 rounded-full -mr-16 -mt-16 pointer-events-none"></div>
        <h2 className="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2 relative z-10">
          <ShieldCheck className="w-5 h-5 text-brand-600" />
          Link giới thiệu (TradingView ID)
        </h2>
        <div className="flex flex-col sm:flex-row gap-3 relative z-10">
          <input readOnly value={refLink} className="flex-1 bg-slate-50 border border-slate-200 rounded-lg py-3.5 px-4 text-slate-600 font-mono text-sm outline-none" />
          <button onClick={handleCopy} className={`flex items-center justify-center gap-2 px-8 py-3.5 rounded-xl font-bold text-white transition-all shadow-lg ${copied ? 'bg-green-600 shadow-green-600/20' : 'bg-brand-600 shadow-brand-600/20 hover:scale-[1.02]'}`}>
            {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            {copied ? 'Đã copy!' : 'Copy Link'}
          </button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-100">
          <p className="text-[11px] font-black text-slate-400 uppercase tracking-wider">Ví Thu Nhập</p>
          <h3 className="text-3xl font-black text-emerald-600 mt-2">${stats.incomeBalance.toLocaleString()}</h3>
          <p className="text-[10px] text-slate-400 mt-3 font-medium">Hoa hồng trực tiếp (T)</p>
        </div>
        <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-100">
          <p className="text-[11px] font-black text-slate-400 uppercase tracking-wider">Ví Thưởng</p>
          <h3 className="text-3xl font-black text-orange-500 mt-2">${stats.rewardBalance.toLocaleString()}</h3>
          <p className="text-[10px] text-slate-400 mt-3 font-medium">Hoa hồng Hệ thống (K)</p>
        </div>
        <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-100">
          <p className="text-[11px] font-black text-slate-400 uppercase tracking-wider">Tổng Sales</p>
          <h3 className="text-3xl font-black text-slate-800 mt-2">{stats.sales}</h3>
          <p className="text-[10px] text-slate-400 mt-3 font-medium">Từ {stats.clicks} lượt click</p>
        </div>
        <div className="bg-gradient-to-br from-[#0F1623] to-[#1F2937] p-6 rounded-xl text-white relative overflow-hidden shadow-xl">
          <div className="relative z-10">
            <p className="text-[11px] font-black text-slate-400 uppercase tracking-wider">Cấp bậc</p>
            <h3 className="text-2xl font-black text-yellow-400 mt-2">{getTierName(stats.currentTier)}</h3>
            <div className="mt-3 text-[10px] text-slate-400 font-bold flex items-center gap-1.5 uppercase">
               <Users className="w-3.5 h-3.5 text-blue-400"/> Group #{stats.groupId || 'N/A'}
            </div>
          </div>
          <ShieldCheck className="absolute -right-4 -bottom-4 w-24 h-24 text-white/5 rotate-12" />
        </div>
      </div>

      {/* RENEWAL SECTION */}
      <div className="bg-white p-6 rounded-xl border border-slate-100 shadow-sm overflow-hidden">
        <div className="flex justify-between items-center mb-6">
            <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                <Zap className="w-5 h-5 text-orange-500" /> Gia Hạn Tài Khoản (Dùng Ví Thưởng)
            </h3>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {PACKAGES.map(pkg => (
                <div key={pkg.id} className="border border-slate-100 rounded-xl p-5 hover:border-brand-500 transition-all bg-slate-50/30">
                    <h4 className="font-bold text-slate-600 text-xs uppercase mb-1 tracking-wider">{pkg.name}</h4>
                    <p className="text-3xl font-black text-slate-800 mb-6">${pkg.price}</p>
                    <button 
                        onClick={() => handleRenew(pkg.id)}
                        className="w-full bg-slate-900 text-white py-2.5 rounded-lg text-sm font-bold hover:bg-brand-600 transition-colors shadow-md shadow-black/10"
                    >
                        Gia hạn ngay
                    </button>
                </div>
            ))}
        </div>
      </div>

      {/* History Sections */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden">
            <div className="p-4 border-b border-slate-50 bg-slate-50/50">
                <h3 className="font-bold text-slate-800 text-sm uppercase tracking-wider">Lịch sử giao dịch</h3>
            </div>
            <div className="divide-y divide-slate-50">
                {transactions.filter(t => t.customerId === currentUser?.id || t.affiliateId === currentUser?.id).length === 0 ? (
                    <div className="p-8 text-center text-slate-400 text-sm">Chưa có giao dịch nào.</div>
                ) : transactions.filter(t => t.customerId === currentUser?.id || t.affiliateId === currentUser?.id).slice(0, 5).map(txn => (
                    <div key={txn.id} className="p-4 flex justify-between items-center hover:bg-slate-50 transition-colors">
                        <div>
                            <p className="font-bold text-slate-800 text-sm">{txn.product}</p>
                            <p className="text-[10px] text-slate-400 font-medium">{txn.date}</p>
                        </div>
                        <div className="text-right">
                            <p className="font-bold text-slate-800 text-sm">${txn.amount}</p>
                            <p className={`text-[9px] font-black uppercase tracking-widest ${txn.status === 'APPROVED' ? 'text-blue-500' : 'text-slate-400'}`}>{txn.status}</p>
                        </div>
                    </div>
                ))}
            </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden">
            <div className="p-4 border-b border-slate-50 bg-slate-50/50">
                <h3 className="font-bold text-slate-800 text-sm uppercase tracking-wider">Hoa hồng (T) gần đây</h3>
            </div>
            <div className="divide-y divide-slate-50">
                {transactions.filter(t => t.affiliateId === currentUser?.id && t.status === 'APPROVED').length === 0 ? (
                    <div className="p-8 text-center text-slate-400 text-sm">Chưa có hoa hồng.</div>
                ) : transactions.filter(t => t.affiliateId === currentUser?.id && t.status === 'APPROVED').slice(0, 5).map(txn => (
                    <div key={txn.id} className="p-4 flex justify-between items-center hover:bg-slate-50 transition-colors">
                        <div>
                            <p className="font-bold text-slate-800 text-xs">{txn.customerName}</p>
                            <p className="text-[9px] text-slate-400 font-medium">{txn.date}</p>
                        </div>
                        <div className="text-right">
                            <p className="font-black text-emerald-600 text-sm">+${txn.commission}</p>
                        </div>
                    </div>
                ))}
            </div>
        </div>
      </div>
    </div>
  );
};
